import { useMemo } from 'react';
import { useInfiniteUserImages } from '@/hooks/content-generator/useInfiniteUserImages';

interface UseEditorImageImportProps {
  workbookId?: string; // Still kept for potential future use, but not used in filtering
  searchQuery: string;
  limit?: number;
}

interface EditorImageItem {
  id: string;
  url: string;
  alt_text?: string;
  filename?: string;
  width?: number;
  height?: number;
  user_id?: string;
  workbook_id?: string;
}

/**
 * Hook for importing images in the editor context
 * 
 * UPDATED: Uses new get_user_accessible_images function via useInfiniteUserImages
 * - Server-side access control handles all permissions
 * - Unified image library showing all accessible images
 * - Proper search and pagination support
 */
export const useEditorImageImport = ({
  workbookId, // Kept for potential future use but not used in filtering
  searchQuery,
  limit = 50
}: UseEditorImageImportProps) => {
  // Use the updated useInfiniteUserImages hook with new function
  const imageQuery = useInfiniteUserImages({
    searchQuery,
    limit
  });

  const {
    data,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = imageQuery;

  // Flatten all pages into a single array and transform to EditorImageItem format
  const allImages = useMemo(() => {
    if (!data?.pages) return [];
    
    return data.pages.flatMap(page => 
      page.data.map((image: any): EditorImageItem => ({
        id: image.id,
        url: image.image_url, // Map image_url to url for consistency
        alt_text: image.alt_text,
        filename: image.filename,
        width: image.width,
        height: image.height,
        user_id: image.user_id,
        workbook_id: image.workbook_id
      }))
    );
  }, [data]);

  // Total count from the first page
  const totalCount = useMemo(() => {
    if (!data?.pages || data.pages.length === 0) return 0;
    return data.pages[0]?.totalCount || allImages.length;
  }, [data, allImages.length]);

  return {
    data: allImages,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    totalCount,
    error
  };
}; 